import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface AccountGroup {
  id: string;
  name: string;
  description: string | null;
  color: string;
  icon: string;
  created_at: string;
  accounts: GroupAccount[];
}

export interface GroupAccount {
  id: string;
  name: string;
  type: string;
  color: string;
}

export function useAccountGroups() {
  const [groups, setGroups] = useState<AccountGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchGroups = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch groups
      const { data: groupsData, error: groupsError } = await (supabase as any)
        .from('account_groups')
        .select('*')
        .order('name');

      if (groupsError) throw groupsError;

      // Fetch members with account details for each group
      const groupsWithAccounts = await Promise.all(
        (groupsData || []).map(async (group: any) => {
          const { data: members } = await (supabase as any)
            .from('account_group_members')
            .select('account_id')
            .eq('group_id', group.id);

          // Get account details for these member IDs
          const accountIds = (members || []).map((m: any) => m.account_id);
          let accounts: GroupAccount[] = [];

          if (accountIds.length > 0) {
            const { data: accountsData } = await supabase
              .from('accounts')
              .select('id, name, type, color')
              .in('id', accountIds);
            accounts = (accountsData || []) as GroupAccount[];
          }

          return { ...group, accounts };
        })
      );

      setGroups(groupsWithAccounts);
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const createGroup = async (data: {
    name: string;
    description?: string;
    color: string;
    icon: string;
    accountIds: string[];
  }) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario nao autenticado');

      const { data: newGroup, error } = await (supabase as any)
        .from('account_groups')
        .insert({
          user_id: user.id,
          name: data.name,
          description: data.description || null,
          color: data.color,
          icon: data.icon,
        })
        .select()
        .single();

      if (error) throw error;

      // Add accounts to group
      if (data.accountIds.length > 0) {
        const { error: membersError } = await (supabase as any)
          .from('account_group_members')
          .insert(
            data.accountIds.map(accountId => ({
              group_id: newGroup.id,
              account_id: accountId,
            }))
          );
        if (membersError) throw membersError;
      }

      toast({
        title: 'Grupo criado!',
        description: `${data.name} foi criado com sucesso.`,
      });

      await fetchGroups();
      return newGroup;
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao criar grupo',
        description: error.message,
      });
      return null;
    }
  };

  const updateGroup = async (
    groupId: string,
    data: {
      name: string;
      description?: string;
      color: string;
      icon: string;
      accountIds: string[];
    }
  ) => {
    try {
      const { error } = await (supabase as any)
        .from('account_groups')
        .update({
          name: data.name,
          description: data.description || null,
          color: data.color,
          icon: data.icon,
        })
        .eq('id', groupId);

      if (error) throw error;

      // Replace members: delete all, then re-insert
      await (supabase as any)
        .from('account_group_members')
        .delete()
        .eq('group_id', groupId);

      if (data.accountIds.length > 0) {
        const { error: membersError } = await (supabase as any)
          .from('account_group_members')
          .insert(
            data.accountIds.map(accountId => ({
              group_id: groupId,
              account_id: accountId,
            }))
          );
        if (membersError) throw membersError;
      }

      toast({
        title: 'Grupo atualizado!',
        description: `${data.name} foi atualizado com sucesso.`,
      });

      await fetchGroups();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar grupo',
        description: error.message,
      });
    }
  };

  const deleteGroup = async (groupId: string) => {
    try {
      const { error } = await (supabase as any)
        .from('account_groups')
        .delete()
        .eq('id', groupId);

      if (error) throw error;

      toast({
        title: 'Grupo excluido',
        description: 'O grupo foi removido com sucesso.',
      });

      await fetchGroups();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao excluir grupo',
        description: error.message,
      });
    }
  };

  return {
    groups,
    loading,
    refetch: fetchGroups,
    createGroup,
    updateGroup,
    deleteGroup,
  };
}
